import { Badge, Banner, Button, Field, Input } from '@cloudflare/kumo';
import { FormPage, FormSection } from '../components/ui/FormPage';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ICONS } from '../components/ui/icons';
import { useTenant } from '../contexts/TenantContext';
import { useAuthenticatedFetch } from '../hooks/useAuthenticatedFetch';
import { useAppToast } from '../hooks/useAppToast';

interface SequenceRow {
  id: string; name: string; active: boolean; steps: number; connectionNames: string[];
  enrollments: Partial<Record<'ACTIVE' | 'COMPLETED' | 'CANCELED' | 'FAILED', number>>;
}

/** Campanhas › Sequências */
export function SequencesPage() {
  const { can } = useTenant();
  const fetchApi = useAuthenticatedFetch();
  const toast = useAppToast();
  const navigate = useNavigate();
  const canEdit = can('campaigns:write');
  const [rows, setRows] = useState<SequenceRow[] | null>(null);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const res = await fetchApi('/api/sequences');
    if (res.ok) setRows((await res.json()).sequences);
  }, [fetchApi]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setSaving(true);
    const res = await fetchApi('/api/sequences', { method: 'POST', body: JSON.stringify({ name }) });
    const j = await res.json().catch(() => ({}));
    setSaving(false);
    if (!res.ok) { toast.error(j.message || 'Não foi possível criar'); return; }
    navigate(`/sequencias/${j.sequence.id}`);
  };

  if (creating) {
    return (
      <FormPage
        backTo="/sequencias"
        backLabel="Sequências"
        onBack={() => setCreating(false)}
        title={'Nova sequência'}
        description="Uma sequência envia mensagens em passos, no tempo certo."
        submitLabel={'Criar e montar os passos'}
        submitting={saving}
        submitDisabled={!name.trim()}
        onSubmit={create}
        onCancel={() => setCreating(false)}
      >
        <FormSection title="Detalhes">


        <Field label="Nome">
          <Input onChange={(e) => setName(e.target.value)} value={name} placeholder="Ex.: Boas-vindas de novos leads" />
        </Field>
        
        </FormSection>
      </FormPage>
    );
  }

  return (
    <>
      <div style={{ padding: '28px 40px 0', marginBottom: 24 }} className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold" style={{ marginBottom: 4, fontSize: 24, fontWeight: 600 }}>
            Sequências
          </h2>
          <div style={{ color: 'var(--text-muted)' }}>
            Mensagens em passos para cada contato inscrito: boas-vindas, lembretes, nutrição de leads. Cada passo sai depois do atraso
            e só dentro do horário que você escolher.
          </div>
        </div>
        {canEdit && (
          <Button variant="primary" icon={ICONS.Plus} onClick={() => { setName(''); setCreating(true); }}>
            Nova sequência
          </Button>
        )}
      </div>

      <div className="page-content space-y-3">
        {rows && rows.length === 0 && (
          <Banner variant="default" description={<>Nenhuma sequência ainda. Crie a primeira e defina os passos.</>} />
        )}
        {rows?.map((s) => {
          const e = s.enrollments;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => navigate(`/sequencias/${s.id}`)}
              className="w-full text-left flex items-center justify-between gap-4"
              style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: '14px 20px', cursor: 'pointer' }}
            >
              <div style={{ minWidth: 0 }}>
                <div className="flex items-center gap-2">
                  <div className="font-semibold">{s.name}</div>
                  {s.active ? <Badge variant="green">Ativa</Badge> : <Badge variant="neutral">Pausada</Badge>}
                </div>
                <div className="text-xs !text-[var(--text-muted)]">
                  {s.steps} passo{s.steps === 1 ? '' : 's'} · {s.connectionNames.length} conex{s.connectionNames.length === 1 ? 'ão' : 'ões'}
                </div>
              </div>
              <div className="flex gap-6 text-right" style={{ flexShrink: 0 }}>
                <div><strong>{(e.ACTIVE ?? 0).toLocaleString('pt-BR')}</strong><div className="text-xs !text-[var(--text-muted)]">em andamento</div></div>
                <div><strong>{(e.COMPLETED ?? 0).toLocaleString('pt-BR')}</strong><div className="text-xs !text-[var(--text-muted)]">concluíram</div></div>
                <div><strong>{((e.CANCELED ?? 0) + (e.FAILED ?? 0)).toLocaleString('pt-BR')}</strong><div className="text-xs !text-[var(--text-muted)]">saíram</div></div>
              </div>
            </button>
          );
        })}
      </div>


    </>
  );
}
